// Section 19 and 14: Refugees. 8% per season, Spring and Autumn bias; more often above trust 75,
// half as often under the founders-first norm, never at the population cap. 2 to 8 people fleeing
// famine or raiders arrive as a family (a couple, their children and kin; one household), weakened
// by the road. The gate asks (Admit all, Admit some, Admit as guests, Turn away) or the migration
// policy decides. Constants: config.events.refugees, config.migration.
import type { Config } from '../../config';
import {
  arrivalChoices,
  arrivalRoom,
  arrivalWeight,
  eventEntry,
  eventRng,
} from '../../core/content-helpers';
import { defineEvent, noModifiers } from '../../core/registry';
import type { ArrivalReason, ReadContext } from '../../core/types';

interface Plan {
  size: number;
  reason: ArrivalReason;
  seed: number;
}

export function refugees(config: Config) {
  const c = config.events.refugees;
  const plan = (ctx: ReadContext): Plan => {
    const rng = eventRng(ctx, 'refugees', 'plan');
    const size = Math.min(rng.int(c.minSize, c.maxSize), arrivalRoom(ctx));
    const reason: ArrivalReason =
      rng.float() < ctx.cfg.migration.famineShare ? 'famine' : 'raiders';
    return { size, reason, seed: Math.floor(rng.float() * 4294967296) >>> 0 };
  };
  return defineEvent({
    id: 'refugees',
    label: 'Refugees',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring', 'autumn'],
    foreshadow: null,
    ledgerTag: 'gate',
    weight: (ctx) => arrivalWeight(ctx, c.baseChance),
    text: (ctx) => {
      const p = plan(ctx);
      return eventEntry('refugees', { size: p.size, variant: p.reason });
    },
    apply: (ctx) => {
      const p = plan(ctx);
      if (p.size <= 0) return [];
      return [
        {
          kind: 'arrivals',
          spec: {
            eventId: 'refugees',
            reason: p.reason,
            size: p.size,
            composition: 'family',
            skilled: null,
            claims: null,
            seed: p.seed,
          },
        },
      ];
    },
    choices: arrivalChoices('refugees', {
      'admit-all': { id: 'admit-all', label: 'Admit all' },
      'admit-some': { id: 'admit-some', label: 'Admit some' },
      guests: { id: 'guests', label: 'Admit as guests' },
      'turn-away': { id: 'turn-away', label: 'Turn away' },
    }),
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
